import { useState } from 'react';
import { Select } from '../UI/Select';
import { Slider } from '../UI/Slider';
import { CollapsiblePanel } from '../UI/CollapsiblePanel';

interface TextClarityTestProps {
  fontSize: number;
  fontFamily: 'sans-serif' | 'serif' | 'monospace';
  letterSpacing: number;
  lineHeight: number;
  onFontSizeChange: (size: number) => void;
  onFontFamilyChange: (family: 'sans-serif' | 'serif' | 'monospace') => void;
  onLetterSpacingChange: (spacing: number) => void;
  onLineHeightChange: (height: number) => void;
}

const sampleBlocks = [
  `The quick brown fox jumps over the lazy dog. ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789 !@#$%^&*()_+-=[]{}|;':",.<>?/`,
  `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.`,
  `iIlL1| oO0Q S5$ Z2 B8 G6 rn m vv w — Distinguish similar characters: Il1| O0Q S5$ Z2 B8 G6. Check subpixel rendering and anti-aliasing quality across your display.`,
  `Pack my box with five dozen liquor jugs. How vexingly quick daft zebras jump! The five boxing wizards jump quickly. Sphinx of black quartz, judge my vow.`,
  `ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789 ¡¢£¤¥¦§¨©ª«¬®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖרÙÚÛÜÝÞß`,
  `Whereas recognition of the inherent dignity and of the equal and inalienable rights of all members of the human family is the foundation of freedom, justice and peace in the world.`,
];

type SchemeKey = 'light' | 'light-gray' | 'dark-gray' | 'black';

const SCHEMES: Record<SchemeKey, {
  page: string;
  tileA: { bg: string; fg: string };
  tileB: { bg: string; fg: string };
  border: string;
}> = {
  'light':      { page: '#ffffff', tileA: { bg: '#ffffff', fg: '#000000' }, tileB: { bg: '#f4f4f4', fg: '#1a1a1a' }, border: 'rgba(0,0,0,0.1)' },
  'light-gray': { page: '#cfcfcf', tileA: { bg: '#d4d4d4', fg: '#000000' }, tileB: { bg: '#c7c7c7', fg: '#0a0a0a' }, border: 'rgba(0,0,0,0.18)' },
  'dark-gray':  { page: '#2b2b2b', tileA: { bg: '#2b2b2b', fg: '#e8e8e8' }, tileB: { bg: '#242424', fg: '#d8d8d8' }, border: 'rgba(255,255,255,0.1)' },
  'black':      { page: '#000000', tileA: { bg: '#000000', fg: '#ffffff' }, tileB: { bg: '#090909', fg: '#f0f0f0' }, border: 'rgba(255,255,255,0.12)' },
};

export function TextClarityTest({
  fontSize,
  fontFamily,
  letterSpacing,
  lineHeight,
  onFontSizeChange,
  onFontFamilyChange,
  onLetterSpacingChange,
  onLineHeightChange,
}: TextClarityTestProps) {
  const [schemeKey, setSchemeKey] = useState<SchemeKey>('light');
  const scheme = SCHEMES[schemeKey];

  const fontFamilyMap = {
    'sans-serif': 'ui-sans-serif, system-ui, sans-serif',
    'serif': 'ui-serif, Georgia, serif',
    'monospace': 'ui-monospace, SFMono-Regular, monospace',
  };

  // Generate enough tiles to fill the screen
  const tileCount = 24;
  const tiles = Array.from({ length: tileCount }, (_, i) => sampleBlocks[i % sampleBlocks.length]);

  // Column width scales with font size (low floor so small sizes visibly change density)
  const columnMin = Math.max(160, Math.round(fontSize * 16));

  return (
    <div className="relative w-full h-full overflow-auto" style={{ backgroundColor: scheme.page }}>
      <div
        className="grid gap-0 w-full min-h-full"
        style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${columnMin}px, 1fr))` }}
      >
        {tiles.map((text, i) => {
          const t = i % 2 === 0 ? scheme.tileA : scheme.tileB;
          return (
            <div
              key={i}
              className="p-4"
              style={{
                fontSize: `${fontSize}px`,
                fontFamily: fontFamilyMap[fontFamily],
                letterSpacing: `${letterSpacing}px`,
                lineHeight: lineHeight,
                color: t.fg,
                backgroundColor: t.bg,
                border: `1px solid ${scheme.border}`,
              }}
            >
              {text}
            </div>
          );
        })}
      </div>

      <CollapsiblePanel title="Text Clarity Test">
        <div className="flex flex-col gap-4">
        <Select
          label="Font Size"
          value={fontSize}
          onChange={(v) => onFontSizeChange(Number(v))}
          options={[
            { value: 6, label: '6px (tiny)' },
            { value: 7, label: '7px' },
            { value: 8, label: '8px' },
            { value: 9, label: '9px' },
            { value: 10, label: '10px' },
            { value: 11, label: '11px' },
            { value: 12, label: '12px' },
            { value: 14, label: '14px' },
            { value: 16, label: '16px' },
            { value: 18, label: '18px' },
            { value: 24, label: '24px' },
            { value: 32, label: '32px' },
            { value: 48, label: '48px' },
            { value: 64, label: '64px' },
          ]}
        />

        <Select
          label="Background"
          value={schemeKey}
          onChange={(v) => setSchemeKey(v as SchemeKey)}
          options={[
            { value: 'light', label: 'White (black text)' },
            { value: 'light-gray', label: 'Light Gray' },
            { value: 'dark-gray', label: 'Dark Gray' },
            { value: 'black', label: 'Reversed — white on black' },
          ]}
        />

        <Select
          label="Font Family"
          value={fontFamily}
          onChange={(v) => onFontFamilyChange(v as typeof fontFamily)}
          options={[
            { value: 'sans-serif', label: 'Sans-serif' },
            { value: 'serif', label: 'Serif' },
            { value: 'monospace', label: 'Monospace' },
          ]}
        />

        <Slider
          min={-2}
          max={10}
          step={0.5}
          value={letterSpacing}
          onChange={onLetterSpacingChange}
          label="Letter Spacing (px)"
        />

        <Slider
          min={1}
          max={3}
          step={0.1}
          value={lineHeight}
          onChange={onLineHeightChange}
          label="Line Height"
        />

        <p className="text-xs text-gray-400">
          Evaluate text sharpness and subpixel rendering. Switch backgrounds to test light, gray, and white-on-black legibility. Look for fuzzy edges or color fringing.
        </p>
        </div>
      </CollapsiblePanel>
    </div>
  );
}
